import React, { useState, useEffect, useRef } from 'react';
import { 
  Menu, X, Download, Copy, Check, Type, MousePointer2, Layout, 
  CreditCard, MessageSquare, ChevronRight, Terminal, CheckCircle2, 
  Star, Briefcase, MapPin, Smile, User, Settings, Calendar, Edit, 
  AlertCircle, HelpCircle, Info, ArrowLeft, Building2, BadgeCheck, 
  CloudUpload, Mail, Zap, FileText, CheckCircle, Loader2, Sparkles, 
  Clock, Share2, UserPlus, Link, Linkedin, ThumbsUp, Lock, IndianRupee, 
  Twitter, Github, Facebook, Instagram, FileUser, Scan, Users, BookOpen, 
  Brain, CircleX, AlertTriangle, List, LogOut, Target, MoreVertical, Home,
  ChevronLeft, ChevronsLeft, ChevronsRight, ChevronDown, LayoutGrid,
  RefreshCw, Replace, MoreHorizontal, Filter, ArrowUp, ArrowDown, Plus, Trash2,
  Bold, Italic, Underline, Link2, ListOrdered, Image as ImageIcon,
  BookMarked, Calculator, Eye, Layers, Monitor, Smartphone, Ruler
} from 'lucide-react';

// ==================================================================================
// 1. MERGED CSS CONTENT (The Single Source of Truth)
// ==================================================================================
const MERGED_STYLES = `
@charset "UTF-8";
@import url("https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css");
/* 2025 Best Practice: Adopting Variable Fonts (Inter) for dynamic weight/width */
@import url("https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap");

:root {
  /* ========================================================= */
  /* 1. PRIMITIVE TOKENS (Raw Visual Values)                   */
  /* ========================================================= */
  --color-blue-500: #0d6efd;
  --color-indigo-100: #eef2ff;
  --color-green-100: #d1e7dd;
  --color-green-700: #198754;
  --color-yellow-100: #fff3cd;
  --color-yellow-700: #856404;
  --color-red-100: #f8d7da;
  --color-red-700: #dc3545;
  
  --color-gray-50: #f5f8ff;
  --color-gray-100: #f2f4f7;
  --color-gray-200: #d0d5dd;
  --color-gray-700: #667085;
  --color-gray-800: #344054;
  --color-gray-900: #212529;

  /* ========================================================= */
  /* 2. SEMANTIC TOKENS (Contextual Roles for Theming)         */
  /* ========================================================= */
  --brand-primary: var(--color-blue-500);
  --brand-accent: #444ce7;
  --brand-bg: var(--color-gray-50);
  --brand-text: var(--color-gray-900);
  
  --color-info-bg: var(--color-indigo-100);
  --color-success-bg: var(--color-green-100);
  --color-success-text: var(--color-green-700);
  --color-warning-bg: var(--color-yellow-100);
  --color-warning-text: var(--color-yellow-700);
  --color-danger-bg: var(--color-red-100);
  --color-danger-text: var(--color-red-700);
  
  --ui-gray-bg: var(--color-gray-100);
  --ui-border: var(--color-gray-200);
  --ui-text-muted: var(--color-gray-700);
  --ui-text-dark: var(--color-gray-800);

  /* --- Report / Print Tokens --- */
  --report-heading: #0a1f44;
  --report-accent: #2f5496;
  --report-link: #325ca5;

  /* --- Dimensions --- */
  --radius-md: 0.5rem;
  --radius-lg: 0.75rem;

  /* ========================================================= */
  /* 3. TYPOGRAPHY: 4 HEADINGS & 2 TEXT SIZES                  */
  /* Ratio: Minor Third (1.125)                                */
  /* ========================================================= */
  --type-ratio: 1.125;
  
  /* Text (2 Sizes) */
  --font-size-base: 0.6875rem; /* ~11px Standard Body */
  --font-size-sm: calc(var(--font-size-base) / var(--type-ratio)); /* ~9.77px Small Text */
  
  /* Headings (4 Sizes) */
  --font-size-h4: calc(var(--font-size-base) * var(--type-ratio) * var(--type-ratio)); /* ~13.92px */
  --font-size-h3: clamp(1rem, 0.9rem + 0.5vw, 1.1875rem); /* ~16-19px */
  --font-size-h2: clamp(1.25rem, 1.1rem + 1vw, 1.5rem); /* ~20-24px */
  --font-size-h1: clamp(1.5rem, 1.2rem + 1.5vw, 1.9375rem); /* ~24-31px */

  /* Enterprise Density Line Spacing */
  --line-height-heading: 1.14; /* Tight for impact/compact blocks */
  --line-height-body: 1.4;     /* Dense but readable body paragraphs */
  --line-height-sm: 1.5;       /* Looser for tiny characters' legibility */

  /* ====== EXTENDED TOKENS ====== */

  /* Colors */
  --color-gray-600: #475467;
  --color-gray-400: #9ca3af;
  --color-peach-bg: #ffe6d5;
  --color-slate-600: #51607A;
  --color-rose-400: #FB7185;
  --color-green-600: #009746;
  --color-blue-deep: #0047FF;
  --color-dark-navy: #1e293b;
  --color-slate-500: #64748b;
  --color-slate-700: #334155;
  --color-slate-400: #475569;
  --color-blue-navy: #1e3a8a;
  --color-highlight-blue: #3b82f6;
  --color-indigo-500: #6366f1;
  --color-sidebar-border: #d7e2ff;
  --color-sidebar-accent: #081da5;

  /* Z-Index Tokenization */
  --ff-z-index-app-header: 100;
  --ff-z-index-dropdown: 1050;
  --ff-z-index-modal: 1100;
  --ff-z-index-toast: 1200;

  /* Typography */
  --font-family-report: "Calibri", Arial, sans-serif;
}

html { 
  scroll-behavior: smooth; 
  font-size: 100%; 
}

body { 
  margin: 0; padding: 0; 
  font-family: 'Inter', sans-serif !important; 
  font-size: var(--font-size-base); 
  background-color: var(--brand-bg); 
  -webkit-font-smoothing: antialiased; 
  line-height: var(--line-height-body); 
  color: var(--brand-text);
  font-weight: 400;
}

/* Responsive Images */
img { max-width: 100%; height: auto; }

/* Print Styles */
@media print {
  .print-footer {
    position: fixed; bottom: 0; left: 0; right: 0;
    text-align: center; font-size: var(--font-size-sm); color: var(--ui-text-muted);
  }
  body { margin-bottom: 30mm; }
  .no-print { display: none !important; }
}

/* Global Utilities */
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 2025 Best Practice: Modular Bento Grid Layouts for Adaptive Density */
.bento-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; align-items: start; }
@media (min-width: 768px) { .bento-grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1024px) { .bento-grid { grid-template-columns: repeat(3, 1fr); } }

/* ========================================= */
/* TYPOGRAPHY GLOBAL STYLES                  */
/* ========================================= */

h1, .h1 { font-size: var(--font-size-h1) !important; line-height: var(--line-height-heading); font-weight: 700; letter-spacing: -0.02em; margin-bottom: 0.5rem; }
h2, .h2 { font-size: var(--font-size-h2) !important; line-height: var(--line-height-heading); font-weight: 700; letter-spacing: -0.01em; margin-bottom: 0.5rem; }
h3, .h3 { font-size: var(--font-size-h3) !important; line-height: var(--line-height-heading); font-weight: 700; margin-bottom: 0.5rem; }
h4, .h4 { font-size: var(--font-size-h4) !important; line-height: var(--line-height-heading); font-weight: 700; margin-bottom: 0.5rem; }

/* Enforcing strictly 2 text sizes */
.text-sm { font-size: var(--font-size-sm) !important; line-height: var(--line-height-sm); }
.text-base { font-size: var(--font-size-base) !important; line-height: var(--line-height-body); }

p {
  font-size: var(--font-size-base); 
  line-height: var(--line-height-body); 
  text-align: left; 
  max-inline-size: 75ch; 
  margin-bottom: 1rem;
}

/* Font Weights */
.fw-bold { font-weight: 700 !important; }
.fw-normal { font-weight: 400 !important; }
.fw-medium { font-weight: 500 !important; }

/* Text Colors */
.text-primary { color: var(--brand-primary) !important; }
.text-danger { color: var(--color-danger-text) !important; }
.text-warning { color: var(--color-warning-text) !important; }
.text-success { color: var(--color-success-text) !important; }
.text-muted { color: var(--ui-text-muted) !important; }
.text-center { text-align: center !important; }

/* Layout & Preview Overrides */
.ds-wrapper { background-color: var(--brand-bg); min-height: 100vh; display: flex; flex-direction: column; min-width: 320px; }
.ds-wrapper.desktop-preview { min-width: 1024px; overflow-x: auto; }

.ds-header { background: #fff; border-bottom: 1px solid var(--ui-border); position: sticky; top: 0; z-index: var(--ff-z-index-app-header); }
.ds-brand-row { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 1.5rem; border-bottom: 1px solid var(--ui-gray-bg); }
.ds-nav-row { padding: 0 1.5rem; overflow-x: auto; white-space: nowrap; background: #fff; }
.ds-content { flex: 1; padding: 2rem; width: 100%; max-width: 1200px; margin: 0 auto; }

/* Tabs */
.nav-tabs-horizontal { display: flex; gap: 2rem; border-bottom: none; margin: 0; padding: 0; list-style: none; }
.nav-item-h { padding: 1rem 0.25rem; cursor: pointer; color: var(--ui-text-muted); font-weight: 500; font-size: var(--font-size-base); border-bottom: 2px solid transparent; transition: all 0.2s; display: flex; align-items: center; gap: 0.5rem; }
@media (hover: hover) { .nav-item-h:hover { color: var(--brand-primary); } }
.nav-item-h.active { color: var(--brand-primary); border-bottom-color: var(--brand-primary); }

/* Buttons */
.btn { font-weight: 600; padding: 0.5rem 1rem; border-radius: var(--radius-md); display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem; transition: 0.2s; font-size: var(--font-size-base); cursor: pointer; border: 1px solid transparent; }
.btn-primary { background-color: var(--brand-primary); border-color: var(--brand-primary); color: #fff; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); }
.btn-primary:disabled { background-color: #93c5fd; border-color: #93c5fd; cursor: not-allowed; transform: none; }
.btn-outline-primary { color: var(--brand-primary); border-color: var(--brand-primary); background: #fff; }
.btn-icon { padding: 8px; color: var(--ui-text-muted); border: 1px solid var(--ui-border); background: #fff; }

.btn-skip {
  background: transparent;
  border: none;
  color: var(--brand-primary);
  font-size: var(--font-size-base);
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 4px;
  text-decoration-color: rgba(13, 110, 253, 0.25);
  transition: all 0.2s ease;
}

/* Action Text Links (Edit, Filter, Clear) */
.text-action-link {
  background: transparent;
  border: none;
  padding: 0;
  color: var(--brand-primary);
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
}

@media (hover: hover) {
  .btn-primary:hover { background-color: #0b5ed7; }
  .btn-outline-primary:hover { background-color: var(--color-info-bg); }
  .btn-icon:hover { color: var(--brand-primary); border-color: var(--brand-primary); background: var(--color-info-bg); }
  .btn-skip:hover { text-decoration-color: var(--brand-primary); }
  .text-action-link:hover { text-decoration: underline; text-underline-offset: 4px; }
}

/* Pagination */
.pagination-wrapper { display: flex; justify-content: flex-end; align-items: center; gap: 1.5rem; padding: 1rem 0; width: 100%; }
.pagination-nav { display: flex; align-items: center; gap: 0.5rem; }
.page-nav-link { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; color: var(--ui-text-muted); cursor: pointer; background: transparent; border: 1px solid transparent; border-radius: 6px; font-size: var(--font-size-base); transition: all 0.2s; }
.page-nav-link.disabled { opacity: 0.4; cursor: not-allowed; pointer-events: none; }
@media (hover: hover) { .page-nav-link:hover { color: var(--brand-primary); background-color: var(--ui-gray-bg); } }
.page-number { font-weight: 500; font-size: var(--font-size-base); color: var(--brand-text); cursor: pointer; }
.page-select-wrapper { position: relative; display: flex; align-items: center; }
.page-select { appearance: none; padding: 4px 24px 4px 8px; border: 1px solid var(--ui-border); border-radius: 6px; background: #fff; font-size: var(--font-size-base); font-weight: 500; color: var(--brand-text); cursor: pointer; min-width: 60px; }
.page-select:focus { outline: none; border-color: var(--brand-primary); }
.page-select-icon { position: absolute; right: 6px; pointer-events: none; width: 12px; height: 12px; color: var(--ui-text-muted); }

/* Forms & Inputs */
.form-control, .form-select { color: var(--ui-text-dark) !important; font-size: var(--font-size-h4) !important; font-weight: 500; border-radius: 8px !important; min-height: 38px !important; border: 1px solid var(--ui-border) !important; padding: 0.375rem 0.75rem !important; background: #fff; width: 100%; }
.form-control:focus { outline: none; border-color: var(--brand-primary); box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.15); }
.form-label { font-size: var(--font-size-h4); font-weight: 500; color: #344054; margin-bottom: 6px; display: block; }
.otp-input { width: 50px; height: 50px; text-align: center; font-size: var(--font-size-h2); font-weight: 600; border-radius: 8px; border: 1px solid var(--ui-border); background: #fff; color: var(--brand-text); }
.otp-input:focus { border-color: var(--brand-primary); box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.15); outline: none; }
.input-title-large { display: block; width: 100%; padding: 12px 16px; font-size: var(--font-size-h2); font-weight: 600; line-height: 1.2; color: var(--brand-text); background-color: #fff; border: 1px solid var(--ui-border); border-radius: var(--radius-md); transition: all 0.2s ease-in-out; }
.input-title-large:focus { border-color: var(--brand-primary); box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.15); outline: none; }

/* Custom Toggle Switch */
.custom-switch-wrap { display: flex; align-items: center; cursor: pointer; user-select: none; gap: 8px; }
.switch-input { position: absolute; opacity: 0; width: 0; height: 0; }
.switch-slider { position: relative; display: inline-block; width: 36px; height: 20px; background-color: var(--color-gray-200); border-radius: 34px; transition: background-color 0.3s; }
.switch-slider::before { position: absolute; content: ""; height: 16px; width: 16px; left: 2px; bottom: 2px; background-color: white; transition: transform 0.3s; border-radius: 50%; box-shadow: 0 1px 2px rgba(0,0,0,0.2); }
.switch-input:checked + .switch-slider { background-color: var(--color-success-text); }
.switch-input:checked + .switch-slider::before { transform: translateX(16px); }

/* Custom Checkbox & Radio */
.custom-checkbox-wrap { display: flex; align-items: center; gap: 8px; cursor: pointer; user-select: none; }
.custom-checkbox { width: 18px; height: 18px; border-radius: 4px; border: 1px solid var(--ui-border); appearance: none; -webkit-appearance: none; outline: none; cursor: pointer; position: relative; background-color: #fff; transition: all 0.2s; flex-shrink: 0; }
.custom-checkbox:checked { border-color: var(--brand-primary); background-color: var(--color-info-bg); }
.custom-checkbox:checked::after { content: "✓"; font-size: 12px; font-weight: bold; color: var(--brand-primary); position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); }
.custom-radio { width: 18px; height: 18px; appearance: none; -webkit-appearance: none; border: 1px solid var(--ui-border); border-radius: 50%; outline: none; cursor: pointer; position: relative; background-color: #fff; transition: all 0.2s; flex-shrink: 0;}
.custom-radio:checked { border-color: var(--brand-primary); border-width: 5px; }
.checkbox-label { font-size: var(--font-size-base); color: var(--brand-text); font-weight: 500; }

/* Input Utility Classes */
.input-action-pr { padding-right: 30px; }
.input-with-action-icon { position: absolute; top: 50%; right: 12px; transform: translateY(-50%); color: var(--ui-text-muted); cursor: pointer; }
.input-with-action-icon:hover { color: var(--brand-primary); }

/* Tag Selector Component Styles */
.tag-selector-wrap { position: relative; display: inline-block; text-align: left; min-width: 256px; }
.btn-tag-toggle { display: flex; align-items: center; justify-content: space-between; width: 100%; padding: 8px 12px; background: #fff; border: 1px solid var(--ui-border); border-radius: 6px; box-shadow: 0 1px 2px rgba(0,0,0,0.05); cursor: pointer; transition: all 0.2s ease; }
.btn-tag-toggle:focus { outline: none; border-color: var(--brand-primary); box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.15); }
.btn-tag-toggle:hover { background-color: #f9fafb; }
.tag-dropdown-menu { position: absolute; left: 0; top: 100%; z-index: var(--ff-z-index-dropdown); margin-top: 4px; min-width: 100%; width: max-content; background: #fff; border: 1px solid var(--ui-border); border-radius: 6px; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); display: flex; flex-direction: column; overflow: hidden; }
.tag-list { padding: 4px; max-height: 224px; overflow-y: auto; scrollbar-width: thin; scrollbar-color: #888 #f0f0f0; }
.tag-label-item { display: flex; align-items: center; padding: 4px; cursor: pointer; border-radius: 4px; transition: background-color 0.2s; margin-bottom: 2px; user-select: none; white-space: nowrap; }
@media (hover: hover) { .tag-label-item:hover { background-color: var(--ui-gray-bg); } }
.tag-checkbox { width: 14px; height: 14px; margin-right: 6px; accent-color: var(--brand-primary); cursor: pointer; border: 1px solid var(--ui-border); border-radius: 4px; flex-shrink: 0; }
.tag-text { font-size: var(--font-size-sm); color: var(--ui-text-dark); line-height: 1.25; white-space: nowrap; }
.tag-add-section { padding: 8px 12px; border-top: 1px solid var(--ui-gray-bg); position: relative; display: flex; align-items: center; }
.tag-add-input { width: 100%; padding: 6px 32px 6px 8px; font-size: var(--font-size-sm); line-height: 1.25; border: 1px solid var(--ui-border); border-radius: 4px; outline: none; }
.tag-add-input:focus { border-color: var(--brand-primary); box-shadow: 0 0 0 1px var(--brand-primary); }
.tag-add-btn { position: absolute; right: 20px; display: flex; align-items: center; justify-content: center; font-size: 20px; font-weight: bold; color: var(--ui-text-muted); background: none; border: none; cursor: pointer; outline: none; transition: color 0.2s; }
.tag-add-btn:hover { color: var(--brand-primary); }
.tag-footer { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; border-top: 1px solid var(--ui-border); background-color: #f9fafb; gap: 8px; }
.btn-tag-reset { padding: 4px 12px; background-color: #fff; color: var(--brand-primary); font-size: var(--font-size-xs); font-weight: 500; border-radius: 4px; border: 1px solid var(--brand-primary); cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05); transition: all 0.2s; }
.btn-tag-apply { padding: 4px 12px; background-color: var(--brand-primary); color: #fff; font-size: var(--font-size-xs); font-weight: 500; border-radius: 4px; border: 1px solid var(--brand-primary); cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05); transition: all 0.2s; }
.btn-tag-apply:disabled { opacity: 0.5; cursor: not-allowed; }
@media (hover: hover) { 
  .btn-tag-reset:hover { background-color: var(--color-info-bg); }
  .btn-tag-apply:not(:disabled):hover { background-color: #0b5ed7; border-color: #0b5ed7; }
}

/* Touch Target Expansion */
@media (pointer: coarse) {
  .btn, .form-control, .form-select, .page-nav-link, .nav-item-h, .context-menu-item, .btn-skip, .text-action-link, .custom-switch-wrap, .custom-checkbox-wrap, .btn-tag-toggle, .tag-label-item {
    min-height: 44px; padding-top: 10px; padding-bottom: 10px;
  }
}

/* Topic Chips */
.topic-chip { display: inline-flex; align-items: center; padding: 4px 12px; border-radius: 50rem; font-size: var(--font-size-base); font-weight: 500; cursor: pointer; border: 1px solid var(--ui-border); background-color: #fff; color: var(--brand-text); transition: all 0.2s ease; }
@media (hover: hover) { .topic-chip:hover { background-color: var(--ui-gray-bg); } }
.topic-chip.selected { background-color: var(--brand-primary); border-color: var(--brand-primary); color: #fff; }

/* Contextual Menu */
.context-menu { background: #fff; border: 1px solid var(--ui-border); border-radius: 8px; box-shadow: 0 4px 12px -2px rgba(0,0,0,0.1); min-width: 160px; padding: 4px 0; overflow: hidden; }
.context-menu-item { display: flex; align-items: center; gap: 8px; padding: 6px 12px; font-size: var(--font-size-base); font-weight: 500; color: var(--brand-text); cursor: pointer; transition: 0.1s; white-space: nowrap; }
.context-menu-item.danger { color: var(--color-danger-text); }
@media (hover: hover) {
  .context-menu-item:hover { background-color: var(--ui-gray-bg); color: var(--brand-primary); }
  .context-menu-item.danger:hover { background-color: #fef2f2; }
}

/* Badges (Mathematically Scaled - Small Text) */
.badge { display: inline-flex; align-items: center; padding: 0.25em 0.6em; border-radius: 9999px; font-size: var(--font-size-sm); font-weight: 600; line-height: 1; border: 1px solid transparent; white-space: nowrap; }
.badge-excellent { background-color: var(--color-success-bg); color: var(--color-success-text); border-color: var(--color-success-bg); }
.badge-good { background-color: var(--color-info-bg); color: var(--brand-primary); border-color: var(--color-info-bg); }
.badge-normal { background-color: var(--color-warning-bg); color: var(--color-warning-text); border-color: var(--color-warning-bg); }
.badge-poor { background-color: var(--color-danger-bg); color: var(--color-danger-text); border-color: var(--color-danger-bg); }

/* Cards & Modules */
.card-std { background: #fff; border: 1px solid var(--ui-border); border-radius: 12px; padding: 16px; transition: 0.3s; position: relative; margin-bottom: 1rem; height: 100%; }
.card-interactive { cursor: pointer; transition: all 0.2s; border: 1px solid transparent; }
@media (hover: hover) { .card-interactive:hover { border-color: var(--brand-primary) !important; background-color: #f8faff; transform: translateY(-2px); } }
.card-interactive.selected { border-color: var(--brand-primary) !important; background-color: var(--color-info-bg); }
.card-select { background: #fff; border: 1px solid var(--ui-border); border-radius: 12px; padding: 20px; cursor: pointer; transition: 0.2s; position: relative; }
@media (hover: hover) { .card-select:hover { border-color: var(--brand-primary); transform: translateY(-2px); } }
.card-select.selected { border-color: var(--brand-primary); background-color: var(--color-info-bg); box-shadow: 0 0 0 1px var(--brand-primary); }
.card-select-header { text-align: center; margin-bottom: 8px; }
.information-card { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 6px 12px; border-radius: 6px; background-color: transparent; font-style: italic; color: var(--color-warning-text); border: 1px solid var(--color-warning-text); font-size: var(--font-size-base); font-weight: 600; line-height: 1.25; }
.card-pricing { border: 1px solid var(--ui-border); border-radius: 12px; padding: 1.5rem; background: #fff; transition: all 0.2s; height: 100%; position: relative; display: flex; flex-direction: column; justify-content: space-between; }
@media (hover: hover) { .card-pricing:hover { transform: translateY(-4px); box-shadow: 0 12px 24px rgba(0,0,0,0.1); } }
.card-pricing.standard { border-color: var(--brand-primary); background-color: #f8faff; }
.card-pricing.premium { border-color: var(--color-success-text); background-color: #f0fdf4; }
.badge-floating { position: absolute; top: 0; left: 50%; transform: translate(-50%, -50%); background-color: var(--color-success-text); color: white; font-size: var(--font-size-sm); font-weight: 600; padding: 4px 12px; border-radius: 20px; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }

/* Page Container (White Box with Soft Shade) */
.content-area {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid var(--ui-border);
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.04), 0px 1px 3px rgba(0, 0, 0, 0.02);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.panel-header {
  border-bottom: 1px solid var(--ui-border);
  background: #ffffff;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.assessment-table {
  background: #ffffff;
  border-bottom: 1px solid var(--ui-border);
  flex: 1;
  overflow: auto;
  scrollbar-width: thin; 
  scrollbar-color: #888 #f0f0f0;
}

.chat-window { background: #fff; border: 1px solid var(--ui-border); border-radius: 12px; overflow: hidden; height: 500px; display: flex; flex-direction: column; }
.chat-msg { max-width: 75%; padding: 12px 16px; border-radius: 12px; font-size: var(--font-size-base); line-height: var(--line-height-body); margin-bottom: 10px; }
.chat-msg.received { background: #fff; border: 1px solid var(--ui-border); border-bottom-left-radius: 2px; align-self: flex-start; color: var(--brand-text); }
.chat-msg.sent { background: var(--color-info-bg); color: #1e3a8a; border: 1px solid #bfdbfe; border-bottom-right-radius: 2px; align-self: flex-end; margin-left: auto; }
.VWOcard { position: relative; width: 100%; max-width: 160px; height: 160px; background-color: #ffffff; border-radius: 8px; padding: 16px 0; box-shadow: 0px 4px 6px 2px rgba(16,24,40,0.03); display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 12px; border: 1px solid transparent; transition: all 0.2s; cursor: pointer;}
.VWOcard:hover { border-color: var(--brand-primary); }
.orngBrdr { border: 2px solid #f97066; }
.vwo-icon-wrapper { width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background-color: var(--color-info-bg); color: var(--brand-primary); }
.vwo-icon-wrapper.danger { background-color: var(--color-danger-bg); color: var(--color-danger-text); }

/* Ant Table Styles (Mathematical Scale & Sticky Header) */
.table-container { border: 1px solid var(--ui-border); border-radius: 8px; overflow: auto; background: #fff; max-height: 400px; scrollbar-width: thin; scrollbar-color: #888 #f0f0f0; }
.ant-table { width: 100%; border-collapse: collapse; }
/* Sticky Table Header */
.ant-table th { 
  background: #f9fafb; 
  padding: 10px 16px; 
  text-align: left; 
  font-weight: 600; 
  font-size: var(--font-size-base); 
  color: var(--brand-text); 
  border-bottom: 1px solid var(--ui-border); 
  position: sticky; 
  top: 0; 
  z-index: 10;
}
/* Table Content */
.ant-table td { padding: 12px 16px; font-size: var(--font-size-base); color: var(--ui-text-dark); border-bottom: 1px solid var(--ui-border); vertical-align: middle; }
.ant-table tr:last-child td { border-bottom: none; }
@media (hover: hover) { .ant-table tbody tr:hover td { background-color: #f9fafb; cursor: default;} }
.ant-header-content { display: flex; align-items: center; justify-content: space-between; gap: 8px; }

/* Table Icons & Filters (Small Text scaling) */
.ant-icons { display: flex; flex-direction: column; gap: 0px; color: var(--ui-text-muted); cursor: pointer;}
.ant-icon-up, .ant-icon-down { width: var(--font-size-sm); height: var(--font-size-sm); opacity: 0.5; }
.ant-icon-active { opacity: 1; color: var(--brand-primary); }
.ant-filter { color: var(--ui-text-muted); width: var(--font-size-base); height: var(--font-size-base); opacity: 0.6; cursor: pointer; }
@media (hover: hover) { .ant-filter:hover { opacity: 1; color: var(--brand-primary); } }

/* Rich Text Editor */
.rich-text-editor { border: 1px solid var(--ui-border); border-radius: var(--radius-md); overflow: hidden; background: #fff; }
.rte-toolbar { display: flex; gap: 8px; padding: 8px; border-bottom: 1px solid var(--ui-border); background: #f8f9fa; }
.rte-btn { padding: 4px; border-radius: 4px; color: var(--ui-text-muted); cursor: pointer; }
@media (hover: hover) { .rte-btn:hover { background: var(--ui-gray-bg); color: var(--brand-primary); } }
.rte-btn.active { background: var(--ui-gray-bg); color: var(--brand-primary); }
.rte-content { padding: 16px; min-height: 120px; color: var(--brand-text); font-size: var(--font-size-base); outline: none; }

/* MODAL STYLES & Glassmorphism */
.custom-modal-overlay { position: fixed; inset: 0; background-color: rgba(0,0,0,0.5); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); z-index: var(--ff-z-index-modal); display: flex; align-items: center; justify-content: center; animation: fadeIn 0.2s ease-out; }
.custom-modal-dialog { background: #fff; width: 100%; max-width: 500px; max-height: 90vh; border-radius: var(--radius-lg); box-shadow: 0 10px 25px rgba(0,0,0,0.1); display: flex; flex-direction: column; overflow: hidden; animation: slideUp 0.3s ease-out; }
.custom-modal-header { padding: 1rem 1.5rem; border-bottom: 1px solid var(--ui-border); display: flex; justify-content: space-between; align-items: center; background: #fff; }
.custom-modal-body { padding: 1.5rem; flex: 1; overflow-y: auto; background: #fff; scrollbar-width: thin; }
.custom-modal-footer { padding: 1rem 1.5rem; border-top: 1px solid var(--ui-border); background: #f9fafb; display: flex; justify-content: flex-end; gap: 1rem; }
.anim-fade { animation: fadeIn 0.3s ease-out; }

/* Sticky Footer Glassmorphism */
.sticky-action-bar { padding: 1rem 1.5rem; border-top: 1px solid var(--ui-border); background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); flex-shrink: 0; display: flex; justify-content: space-between; align-items: center; z-index: 10; }

/* Toasts */
.toast-container { position: absolute; bottom: 20px; right: 20px; z-index: var(--ff-z-index-toast); display: flex; flex-direction: column; gap: 10px; }
.toast { background: #fff; border: 1px solid var(--ui-border); border-radius: 8px; padding: 12px 16px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); display: flex; align-items: center; gap: 12px; min-width: 300px; animation: slideInRight 0.3s ease-out; }
@keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
@keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

/* Demo Highlights for Guidelines */
.demo-highlight-primary { background-color: rgba(13, 110, 253, 0.05); border-radius: 4px; padding: 2px; }
.demo-highlight-success { background-color: rgba(25, 135, 84, 0.05); border-radius: 4px; padding: 2px; }
.demo-highlight-danger { background-color: rgba(220, 53, 69, 0.05); border-radius: 4px; padding: 2px; }

/* Empty & Error States */
.empty-preview { color: var(--ui-text-muted); font-style: italic; }
.bottomBorderFieldInvalid { border-bottom: 2px solid var(--color-danger-text) !important; }
`;

// ==================================================================================
// 2. CODE SNIPPETS
// ==================================================================================
const CODE_SNIPPETS = {
  primaryActions: `<!-- Primary Button -->
<button class="btn btn-primary">Primary</button>

<!-- Disabled Button -->
<button class="btn btn-primary" disabled>Inactive</button>

<!-- Secondary Button -->
<button class="btn btn-outline-primary">Secondary</button>`,

  viewToggles: `<!-- Grid View (Inactive) -->
<button class="btn btn-icon"><i class="icon-grid"></i></button>
<!-- List View (Active) -->
<button class="btn btn-primary btn-icon"><i class="icon-list"></i></button>`,

  skipLink: `<!-- Skip Navigation Link -->
<button class="btn-skip">Skip</button>`,

  textLinks: `<!-- Standard Action Link -->
<button class="text-action-link">Edit</button>

<!-- Filter Action Links -->
<div class="d-flex gap-4">
  <button class="text-action-link">Advanced Filter</button>
  <button class="text-action-link text-muted">Clear All</button>
</div>`,

  contextMenu: `<div class="context-menu">
  <div class="context-menu-item">
    <i class="icon-edit"></i> Edit
  </div>
  <div class="context-menu-item">
    <i class="icon-copy"></i> More Like This
  </div>
  <div class="context-menu-item">
    <i class="icon-replace"></i> Replace
  </div>
  <div class="context-menu-item danger">
    <i class="icon-trash"></i> Delete
  </div>
</div>`,

  tabs: `<div class="border-bottom w-100">
  <ul class="nav-tabs-horizontal mb-0">
    <li class="nav-item-h active">Dashboard</li>
    <li class="nav-item-h">Profile</li>
  </ul>
</div>`,

  pagination: `<div class="pagination-wrapper">
  <div class="pagination-nav">
    <button class="page-nav-link disabled"><i class="chevrons-left"></i></button>
    <button class="page-nav-link disabled"><i class="chevron-left"></i></button>
    <span class="page-number">2</span>\n    <button class="page-nav-link"><i class="chevron-right"></i></button>
    <button class="page-nav-link"><i class="chevrons-right"></i></button>
  </div>
  <div class="page-select-wrapper">
    <select class="page-select">
      <option>500</option>
    </select>
    <i class="page-select-icon"></i>
  </div>
</div>`,

  docTitle: `<div class="w-100">
  <label class="form-label text-muted text-sm uppercase fw-bold mb-1">Document Name</label>
  <input type="text" class="input-title-large" value="Q3 Financial Report" />
</div>`,

  topicSelector: `<div class="w-100">
  <h4 class="fw-bold mb-1">1. Set Context</h4>
  <p class="text-sm fw-bold text-muted mb-3">Select main topics (max 5)</p>
  
  <div class="position-relative mb-3">
    <input type="text" class="form-control input-action-pr" placeholder="Add custom topic..." />
    <div class="input-with-action-icon fw-bold" style="font-size: 18px; line-height: 1;">+</div>
  </div>
  
  <div class="d-flex flex-wrap gap-2">
    <div class="topic-chip">first topic</div>
    <div class="topic-chip selected">second topic</div>
    <div class="topic-chip">third topic</div>
    <div class="topic-chip selected">fourth topic</div>
    <div class="topic-chip">fifth topic</div>
  </div>
</div>`,

  tagSelector: `<!-- Tag Selector Dropdown -->
<div class="tag-selector-wrap">
  <!-- Toggle Button -->
  <button type="button" class="btn-tag-toggle">
    <span class="text-sm text-muted">Set Tags</span>
    <i class="icon-chevron-down"></i>
  </button>

  <!-- Dropdown Menu (Open State) -->
  <div class="tag-dropdown-menu">
    
    <!-- Tag List -->
    <div class="tag-list">
      <div class="d-flex justify-content-between align-items-center">
        <label class="tag-label-item flex-grow-1 m-0 mb-1">
          <input type="checkbox" class="tag-checkbox" checked />
          <span class="tag-text">In Progress</span>
        </label>
        <button type="button" class="text-action-link ms-4 me-2 flex-shrink-0" style="font-size: var(--font-size-xs)">Clear All</button>
      </div>
      <div class="d-flex justify-content-between align-items-center">
        <label class="tag-label-item flex-grow-1 m-0 mb-1">
          <input type="checkbox" class="tag-checkbox" />
          <span class="tag-text">Call to Interview</span>
        </label>
      </div>
    </div>

    <!-- Custom Tag Input -->
    <div class="tag-add-section">
      <input type="text" class="tag-add-input" placeholder="Add Custom tag" />
      <button type="button" class="tag-add-btn" aria-label="Add custom tag">+</button>
    </div>

    <!-- Footer Actions -->
    <div class="tag-footer">
      <button type="button" class="btn-tag-reset">Reset</button>
      <button type="button" class="btn-tag-apply" disabled>Apply</button>
    </div>
  </div>
</div>`,

  inputs: `<div class="mb-3">
  <label class="form-label">Email Address</label>
  <input type="email" class="form-control" placeholder="name@example.com" />
</div>
<div class="mb-3">
  <label class="form-label">Password</label>
  <input type="password" class="form-control" placeholder="••••••••" />
</div>`,

  customControls: `<!-- Custom Toggle Switch -->
<label class="custom-switch-wrap mb-3">
  <span class="checkbox-label">Notifications</span>
  <div class="position-relative">
    <input type="checkbox" class="switch-input" checked />
    <span class="switch-slider"></span>
  </div>
</label>

<!-- Custom Checkbox -->
<label class="custom-checkbox-wrap mb-2">
  <input type="checkbox" class="custom-checkbox" checked />
  <span class="checkbox-label">Remember me</span>
</label>

<!-- Custom Radio -->
<label class="custom-checkbox-wrap">
  <input type="radio" name="options" class="custom-radio" checked />
  <span class="checkbox-label">Option A</span>
</label>`,

  otp: `<div class="d-flex justify-content-center gap-2">
  <input class="otp-input" value="4" />
  <input class="otp-input" value="2" />
  <input class="otp-input" />
  <input class="otp-input" />
</div>`,

  rte: `<div class="rich-text-editor">
  <div class="rte-toolbar">
    <i class="icon-bold rte-btn"></i>
    <i class="icon-italic rte-btn"></i>
    <i class="icon-underline rte-btn"></i>
    <span class="border-end mx-1"></span>
    <i class="icon-list rte-btn"></i>
    <i class="icon-list-ordered rte-btn"></i>
    <span class="border-end mx-1"></span>
    <i class="icon-link rte-btn"></i>
    <i class="icon-image rte-btn"></i>
  </div>
  <div class="rte-content" contenteditable="true">
    Start typing your content here...
  </div>
</div>`,

  standardCard: `<div class="card-std">
  <h4 class="fw-bold">Card Title</h4>
  <p class="text-muted m-0">Standard card container content.</p>
</div>`,

  pageContainer: `<!-- Page Container -->
<div class="content-area" style="height: 400px;">
  <div class="panel-header">
    <h3 class="fw-bold m-0">Assessments</h3>
    <button class="btn btn-primary">Create</button>
  </div>
  <div class="assessment-table">
     <table class="ant-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Assessment 1</td><td><span class="badge badge-good">Active</span></td></tr>
          <tr><td>Assessment 2</td><td><span class="badge badge-normal">Draft</span></td></tr>
        </tbody>
     </table>
  </div>
</div>`,

  badges: `<!-- Excellent Match -->
<span class="badge badge-excellent">Excellent</span>

<!-- Good Match -->
<span class="badge badge-good">Good</span>

<!-- Normal Match -->
<span class="badge badge-normal">Normal</span>

<!-- Poor Match -->
<span class="badge badge-poor">Poor</span>`,

  infoCard: `<div class="information-card">
  <span>System Maintenance Scheduled</span>
</div>`,

  resumeCard: `<div class="card-std d-flex gap-3 align-items-start">
  <div class="icon-baseline">
    <i class="icon-star text-warning"></i>
  </div>
  <div>
    <h4 class="fw-bold m-0 text-dark">Proactive Innovator</h4>
    <p class="text-muted text-sm m-0 mt-1">Demonstrated initiative...</p>
  </div>
</div>`,

  selectionCard: `<div class="d-flex flex-column gap-3 w-100">
  <div class="card-select w-100 selected position-relative h-100">
    <div class="card-select-header"><h3 class="fw-bold">Plan Pro</h3></div>
    <p class="text-center text-muted m-0">₹899/mo</p>
    <div class="position-absolute top-0 end-0 p-2 text-primary"><CheckCircle2 size={18} /></div>
  </div>
  <div class="card-select w-100 h-100">
    <div class="card-select-header"><h3 class="fw-bold">Enterprise</h3></div>
    <p class="text-center text-muted m-0">Contact Us</p>
  </div>
</div>`,

  roleCard: `<div class="d-flex flex-column gap-3 justify-content-center align-items-center flex-wrap w-100">
  <div class="VWOcard" style="width: 100%; maxWidth: 200px; minHeight: 120px;">
    <div class="vwo-icon-wrapper">
      <i class="icon-layout"></i>
    </div>
    <p class="text-base m-0">Candidate</p>
  </div>
  <div class="VWOcard orngBrdr" style="width: 100%; maxWidth: 200px; minHeight: 120px;">
    <div class="vwo-icon-wrapper danger">
      <i class="icon-credit-card"></i>
    </div>
    <p class="text-base m-0">Organization</p>
  </div>
</div>`,

  pricingCard: `<div class="row g-4 m-0 w-100">
  <div class="col-12 col-sm-6">
    <div class="card-pricing standard">
      <div>
        <h2 class="fw-bold">Standard</h2>
        ...
      </div>
      <button class="btn btn-outline-primary w-100 mt-4">Choose</button>
    </div>
  </div>
  <div class="col-12 col-sm-6">
    <div class="card-pricing premium">
      <div class="badge-floating">Best Value</div>
      <div>
        <h2 class="fw-bold">Premium</h2>
        ...
      </div>
      <button class="btn btn-primary w-100 mt-4">Go Premium</button>
    </div>
  </div>
</div>`,

  chat: `<div class="chat-msg received">
  Hello! Update available.
  <div class="chat-timestamp">10:00 AM</div>
</div>

<div class="chat-msg sent">
  Great news!
  <div class="chat-timestamp">10:05 AM</div>
</div>`,

  tooltip: `<div class="roadmap-step">
  <i class="help-circle"></i>
  <span class="small fw-medium">Hover Me</span>
  <div class="roadmap-tooltip">...</div>
</div>`,

  listHeader: `<div class="candListBox w-100 p-3" style="margin: 0;">
  <div class="candListHdr border-bottom-none m-0 p-0">
     <div class="d-flex justify-content-between align-items-center w-100">
       <h4 class="m-0 text-md">Active Applications</h4>
       <button class="btn btn-primary btn-sm px-3">+ Add</button>
     </div>
  </div>
  <div class="p-4 text-muted text-center text-sm border-top mt-3">No applications found.</div>
</div>`,

  kanban: `<div class="kanban-board">
  <div class="kanban-col">
    <div class="mb-3 text-base fw-bold text-muted">TO DO</div>
    <div class="kanban-item">Task Name</div>
  </div>
</div>`,

  headings: `<!-- H1: ~24-31px (Fluid) -->
<h1 class="fw-bold">Page Title</h1>

<!-- H2: ~20-24px (Fluid) -->
<h2 class="fw-bold">Section Heading</h2>

<!-- H3: ~16-19px (Fluid) -->
<h3 class="fw-bold">Card Title</h3>

<!-- H4: ~14px (Base * Ratio * Ratio) -->
<h4 class="fw-bold">Sub-Label</h4>`,

  lineHeight: `<!-- Heading: Line-height 1.14 -->
<h3 class="fw-bold mb-3 m-0 text-dark">
  Enterprise Dashboard Overview<br/>
  Q3 Performance Metrics
</h3>

<!-- Body: Line-height 1.4 -->
<p class="text-base text-muted m-0">
  This dashboard provides a comprehensive view of our quarterly performance.<br/>
  Notice how the generous line spacing makes reading multi-line data easier,<br/>
  while the tighter heading keeps the title visually grouped.
</p>

<!-- Caption: Line-height 1.5 -->
<p class="text-sm text-muted m-0">
  * Note: Small captions use a looser 1.5 line height<br/>
  to remain highly legible at tiny sizes.
</p>`,

  textTruncation: `<!-- Standard text wrapping -->
<p class="text-base border p-2" style="width: 200px;">
  This is a very long text string that will wrap to the next line naturally.
</p>

<!-- Truncated Text using .text-truncate -->
<p class="text-base border p-2 text-truncate" style="width: 200px;">
  This is a very long text string that will be cut off with an ellipsis because it doesn't fit.
</p>`,

  textSizes: `<!-- Standard Body: 11px (0.6875rem) -->
<p class="text-base">Standard Body Text (Regular)</p>

<!-- Small Text: ~9.77px (Base / 1.125) -->
<p class="text-sm">Small Caption Text (Regular)</p>`,

  colors: `<p class="text-primary text-base">Primary (Links/Actions)</p>
<p class="text-danger text-base">Danger (Errors/Deletes)</p>
<p class="text-warning text-base">Warning (Alerts/Pending)</p>
<p class="text-success text-base">Success (Validations)</p>
<p class="text-muted text-base">Muted (Hints/Metadata)</p>`,

  socialIcons: `<Icon name="Linkedin" color="#0077b5" />`,
  
  iconography: `import { IconName } from 'lucide-react';\n<IconName size={24} />`,

  table: `<div class="table-container w-100" style="max-height: 250px;">
  <table class="ant-table">
    <thead>
      <tr>
        <th>
          <div class="ant-header-content">
            Name
            <div class="ant-icons">
               <i class="icon-up ant-icon-active"></i>
               <i class="icon-down"></i>
            </div>
          </div>
        </th>
        <th>Role</th>
        <th>Status</th>
        <th class="text-end">Actions</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>John Doe</td>
        <td>Developer</td>
        <td><span class="badge badge-good">Active</span></td>
        <td class="text-end"><span class="table-action-link">Edit</span></td>
      </tr>
      <tr>
        <td>Jane Smith</td>
        <td>Designer</td>
        <td><span class="badge badge-normal">Pending</span></td>
        <td class="text-end"><span class="table-action-link">Edit</span></td>
      </tr>
      <!-- Extra rows to demonstrate sticky header -->
      <tr><td>Mark Lee</td><td>Manager</td><td><span class="badge badge-good">Active</span></td><td class="text-end"><span class="table-action-link">Edit</span></td></tr>
      <tr><td>Alice Cooper</td><td>Analyst</td><td><span class="badge badge-excellent">Top</span></td><td class="text-end"><span class="table-action-link">Edit</span></td></tr>
      <tr><td>Bob Vance</td><td>DevOps</td><td><span class="badge badge-poor">Offline</span></td><td class="text-end"><span class="table-action-link">Edit</span></td></tr>
    </tbody>
  </table>
</div>`,

  modal: `<!-- Modal Structure -->
<div class="custom-modal-overlay">
  <div class="custom-modal-dialog">
    <div class="custom-modal-header">
      <h3 class="fw-bold m-0">Edit User Details</h3>
      <button class="btn btn-icon"><i class="icon-x"></i></button>
    </div>
    <div class="custom-modal-body">
      <p class="text-muted">Make changes to the user profile here. Click save when you're done.</p>
    </div>
    <div class="custom-modal-footer">
      <button class="btn btn-outline-primary">Cancel</button>
      <button class="btn btn-primary">Save Changes</button>
    </div>
  </div>
</div>`,

  toast: `<div class="toast-container">
  <div class="toast anim-fade">
    <i class="icon-check-circle text-success"></i>
    <div class="d-flex flex-column">
      <span class="fw-bold text-base">Success</span>
      <span class="text-muted text-sm">Changes saved successfully.</span>
    </div>
    <button class="btn-icon ms-auto"><i class="icon-x"></i></button>
  </div>
</div>`,

  stickyBar: `<div class="sticky-action-bar">
  <button class="btn btn-outline-primary">Cancel</button>
  <div class="d-flex align-items-center gap-3">
    <span class="text-base fw-bold">2 Items Selected</span>
    <button class="btn btn-primary">Apply Action</button>
  </div>
</div>`
};

// ==================================================================================
// 3. HELPER COMPONENTS
// ==================================================================================
const CodeBlock = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    // Fallback copy logic to fix strict iFrame permissions
    const textArea = document.createElement("textarea");
    textArea.value = code;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text', err);
    }
    document.body.removeChild(textArea);
  };
  return (
    <div className="card border-secondary bg-dark text-light mt-3">
      <div className="card-header d-flex justify-content-between align-items-center py-2 px-3 border-secondary">
        <small className="text-secondary font-monospace d-flex align-items-center gap-2"><Terminal size={12} /> HTML</small>
        <button onClick={handleCopy} className="btn btn-link btn-sm text-decoration-none text-light p-0 d-flex align-items-center gap-1">
          {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="card-body m-0 p-3 overflow-auto small font-monospace text-light" style={{ backgroundColor: '#1e1e1e' }}>{code}</pre>
    </div>
  );
};

const ComponentViewer = ({ title, description, children, code, fullWidth }) => (
  <div className="card-std" id={title.replace(/\s+/g, '-').toLowerCase()}>
    <div className="mb-4">
      <h3 className="h5 fw-bold m-0 text-xl">{title}</h3>
      <p className="text-muted text-sm mt-1 mb-0">{description}</p>
    </div>
    <div className="p-4 border rounded bg-light d-flex justify-content-center align-items-center position-relative" style={{ minHeight: '160px' }}>
      <div className="w-100" style={{ maxWidth: fullWidth ? '100%' : '700px' }}>{children}</div>
    </div>
    <CodeBlock code={code} />
  </div>
);

const SizeLabel = ({ size }) => (
    <span className="text-danger opacity-50 ms-1 fw-normal" style={{fontSize: '9px', fontFamily: 'monospace', letterSpacing: '0px'}}>
      [{size}]
    </span>
);

// React Component specifically for the Tag Selector Preview
const TagSelectorDemo = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [availableTags, setAvailableTags] = useState([
    'In Progress', 'Interesting', 'Call to Interview', 'Not Relevant', 'Hold', 'Shortlist', 'Select', 'Hire', 'Reject'
  ]);
  const [appliedTags, setAppliedTags] = useState([]);
  const [draftTags, setDraftTags] = useState(['In Progress']);
  const [newTagInput, setNewTagInput] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    if (!isOpen) {
      setDraftTags([...appliedTags]);
      setNewTagInput('');
    }
    setIsOpen(!isOpen);
  };

  const handleCheckboxChange = (tag) => {
    setDraftTags((prev) => 
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleAddTag = () => {
    const trimmedInput = newTagInput.trim();
    if (trimmedInput && !availableTags.includes(trimmedInput)) {
      setAvailableTags([...availableTags, trimmedInput]);
      setDraftTags([...draftTags, trimmedInput]);
      setNewTagInput('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleReset = () => setDraftTags([]);
  
  const handleApply = () => {
    setAppliedTags([...draftTags]);
    setIsOpen(false);
  };

  return (
    <div className="d-flex flex-column align-items-center w-100" style={{ paddingBottom: isOpen ? '400px' : '0', transition: 'padding 0.2s ease' }}>
      <div className="tag-selector-wrap" ref={dropdownRef}>
        <button type="button" onClick={toggleDropdown} className="btn-tag-toggle">
          <span className="text-sm text-muted">Set Tags <SizeLabel size="Base" /></span>
          <ChevronDown size={16} className="text-muted" />
        </button>

        {isOpen && (
          <div className="tag-dropdown-menu">
            <div className="tag-list">
              {availableTags.map((tag, index) => (
                <div key={tag} className="d-flex justify-content-between align-items-center">
                  <label className="tag-label-item flex-grow-1 m-0 mb-1">
                    <input 
                      type="checkbox" 
                      className="tag-checkbox"
                      checked={draftTags.includes(tag)}
                      onChange={() => handleCheckboxChange(tag)}
                    />
                    <span className="tag-text">{tag} <SizeLabel size="Small Text" /></span>
                  </label>
                  {index === 0 && (
                    <button 
                      type="button" 
                      className="text-action-link ms-4 me-2 flex-shrink-0" 
                      style={{fontSize: 'var(--font-size-xs)'}}
                      onClick={() => setDraftTags([])}
                    >
                      Clear All <SizeLabel size="Small Text" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="tag-add-section">
              <input
                type="text"
                className="tag-add-input"
                value={newTagInput}
                onChange={(e) => setNewTagInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add Custom tag"
              />
              <button type="button" onClick={handleAddTag} className="tag-add-btn" aria-label="Add custom tag">
                +
              </button>
            </div>

            <div className="tag-footer">
              <button type="button" onClick={handleReset} className="btn-tag-reset">Reset <SizeLabel size="Small Text" /></button>
              <button 
                type="button" 
                onClick={handleApply} 
                className="btn-tag-apply"
                disabled={draftTags.length === 0}
              >
                Apply <SizeLabel size="Small Text" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================================================================================
// 4. SECTION COMPONENTS (MODULAR)
// ==================================================================================

const BestPracticesSection = () => (
  <div className="d-flex flex-column gap-5">
    <div className="card-std">
      <h3 className="fw-bold mb-4">Enterprise Typography & CSS Best Practices</h3>
      <p className="text-muted mb-4">
        Our style guide enforces a token-based architecture, mathematical typography scale optimized for enterprise complexity, and adaptive layouts.
      </p>

      <div className="bento-grid">
        
        {/* Token Architecture */}
        <div className="p-4 border rounded bg-white h-100">
          <div className="d-flex gap-3 mb-2">
            <div className="text-primary"><Layers size={20} /></div>
            <h4 className="fw-bold m-0">1. Two-Tier Semantic Tokens</h4>
          </div>
          <p className="text-muted text-sm mb-2">Built for dynamic scalability and multi-brand support.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>Primitive Tokens:</strong> Raw visual values (e.g., <code>--color-blue-500: #0d6efd</code>) defined globally.</li>
            <li><strong>Semantic Tokens:</strong> Contextual assignments (e.g., <code>--brand-primary: var(--color-blue-500)</code>) allowing seamless theme swapping without rewriting core CSS.</li>
          </ul>
        </div>

        {/* Typographic Context & Scale */}
        <div className="p-4 border rounded bg-white h-100">
          <div className="d-flex gap-3 mb-2">
            <div className="text-primary"><Calculator size={20} /></div>
            <h4 className="fw-bold m-0">2. Mathematical Type Scale</h4>
          </div>
          <p className="text-muted text-sm mb-2">Limited to 4 Headings and 2 Texts for max consistency.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>11px Baseline:</strong> We use an <code>11px (0.6875rem)</code> baseline for standard body text, fit perfectly for complex enterprise data.</li>
            <li><strong>Ratio (1.125):</strong> <code>Small Text</code> and <code>H4</code> are strictly calculated using the Minor Third ratio over the base size.</li>
          </ul>
        </div>

        {/* Readability & Spacing */}
        <div className="p-4 border rounded bg-white h-100">
          <div className="d-flex gap-3 mb-2">
            <div className="text-success"><Eye size={20} /></div>
            <h4 className="fw-bold m-0">3. High-Density Line Spacing</h4>
          </div>
          <p className="text-muted text-sm mb-2">Optimized for compact enterprise interfaces.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>Body Text (1.4):</strong> Tighter than standard web (1.7) to fit complex data while preventing eye strain.</li>
            <li><strong>Headings (1.14):</strong> Highly compact ratio keeps large text blocks visually grouped.</li>
            <li><strong>Captions (1.5):</strong> Tiny labels require looser spacing to remain legible.</li>
            <li><strong>Truncation:</strong> Use <code>.text-truncate</code> to safely cut off overflowing text in dense tables.</li>
          </ul>
        </div>

        {/* Adaptive Layouts */}
        <div className="p-4 border rounded bg-white h-100">
          <div className="d-flex gap-3 mb-2">
            <div className="text-warning"><Layout size={20} /></div>
            <h4 className="fw-bold m-0">4. Adaptive Bento Layouts</h4>
          </div>
          <p className="text-muted text-sm mb-2">Progressive disclosure and density.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>Bento Grids:</strong> CSS Grid is utilized to create modular, rectangular layouts (<code>.bento-grid</code> class).</li>
            <li><strong>Adaptive Density:</strong> Easily rearrange or prioritize content blocks dynamically to match user context.</li>
            <li><strong>Sticky Headers:</strong> Table headers stick to the top during scrolling to retain context.</li>
          </ul>
        </div>

        {/* Interaction & Glassmorphism */}
        <div className="p-4 border rounded bg-white h-100">
          <div className="d-flex gap-3 mb-2">
            <div className="text-info"><Sparkles size={20} /></div>
            <h4 className="fw-bold m-0">5. Interaction & Depth</h4>
          </div>
          <p className="text-muted text-sm mb-2">Touch-safety and modern aesthetics.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>Hover Safety:</strong> CSS <code>:hover</code> states are isolated within <code>@media (hover: hover)</code> to prevent "sticky" buttons on touchscreens.</li>
            <li><strong>Touch Targets:</strong> On detected touch devices, interactive elements automatically force a minimum height of <strong>44px</strong>.</li>
            <li><strong>Glassmorphism:</strong> Overlay elements utilize <code>backdrop-filter: blur()</code> to generate focus through visual depth.</li>
          </ul>
        </div>

        {/* Z-Index Tokenization */}
        <div className="p-4 border rounded bg-white h-100">
          <div className="d-flex gap-3 mb-2">
            <div className="text-danger"><Layers size={20} /></div>
            <h4 className="fw-bold m-0">6. Z-Index Tokenization</h4>
          </div>
          <p className="text-muted text-sm mb-2">Predictable stacking contexts.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>Centralized Tokens:</strong> Store z-indexes in `:root` (e.g., <code>--ff-z-index-app-header: 100;</code>) to prevent random "Z-Index Wars".</li>
            <li><strong>Strict Layering:</strong> Ensures modals, tooltips, and headers don't unpredictably overlap.</li>
          </ul>
        </div>

        {/* Scrollbar UI Management */}
        <div className="p-4 border rounded bg-white h-100">
          <div className="d-flex gap-3 mb-2">
            <div className="text-secondary"><LayoutGrid size={20} /></div>
            <h4 className="fw-bold m-0">7. Scrollbar UI Management</h4>
          </div>
          <p className="text-muted text-sm mb-2">Clean, unobtrusive scrolling.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>Slim Scrollbars:</strong> Use <code>scrollbar-width: thin;</code> and custom colors to replace clunky OS scrollbars.</li>
            <li><strong>Reduced Noise:</strong> Keeps the focus on data within dense nested tables and Kanban boards.</li>
          </ul>
        </div>

        {/* Empty & Error States */}
        <div className="p-4 border rounded bg-white h-100">
          <div className="d-flex gap-3 mb-2">
            <div className="text-danger"><AlertTriangle size={20} /></div>
            <h4 className="fw-bold m-0">8. "Empty" & "Error" States</h4>
          </div>
          <p className="text-muted text-sm mb-2">Clear semantic UX feedback.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>Empty States:</strong> Use italicized, muted text (<code>.text-muted</code>) for empty tables so users know it's not broken.</li>
            <li><strong>Error States:</strong> Explicit red bottom borders (<code>.bottomBorderFieldInvalid</code>) for missing or invalid form data.</li>
          </ul>
        </div>

      </div>
    </div>
  </div>
);

const NavigationsSection = () => (
  <div className="d-flex flex-column gap-4">
    <div className="bento-grid">
      <ComponentViewer title="Primary Actions" description="Use for main calls-to-action." code={CODE_SNIPPETS.primaryActions}>
        <div className="d-flex gap-3 flex-wrap">
          <button className="btn btn-primary">Primary Action</button>
          <button className="btn btn-primary" disabled>Inactive Action</button>
          <button className="btn btn-outline-primary">Secondary Action</button>
        </div>
      </ComponentViewer>

      <ComponentViewer title="View Toggles" description="Switch between Grid and List view." code={CODE_SNIPPETS.viewToggles}>
        <div className="d-flex gap-2">
          <button className="btn btn-icon"><LayoutGrid size={20} /></button>
          <button className="btn btn-primary btn-icon"><List size={20} /></button>
        </div>
      </ComponentViewer>

      <ComponentViewer title="Skip Navigation" description="Used to skip steps or move ahead quickly." code={CODE_SNIPPETS.skipLink}>
        <div className="d-flex justify-content-center">
          <button className="btn-skip">Skip <SizeLabel size="Base * Ratio" /></button>
        </div>
      </ComponentViewer>

      <ComponentViewer title="Action Text Links" description="Inline hypertext links for table actions, filters, and editing." code={CODE_SNIPPETS.textLinks}>
        <div className="d-flex flex-column gap-3 w-100 bg-white p-4 border rounded">
          <div className="d-flex justify-content-start border-bottom pb-3">
            <button className="text-action-link">Edit <SizeLabel size="Small Text" /></button>
          </div>
          <div className="d-flex flex-wrap gap-4 pt-1">
            <button className="text-action-link">Advanced Filter <SizeLabel size="Small Text" /></button>
            <button className="text-action-link text-muted" style={{color: 'var(--ui-text-muted)'}}>Clear All <SizeLabel size="Small Text" /></button>
          </div>
        </div>
      </ComponentViewer>

      <ComponentViewer title="Contextual Menu" description="Compact action menu with tight spacing." code={CODE_SNIPPETS.contextMenu}>
        <div className="context-menu w-100" style={{maxWidth: '200px'}}>
          <div className="context-menu-item"><Edit size={14} /> Edit <SizeLabel size="Standard Body" /></div>
          <div className="context-menu-item"><Copy size={14} /> More Like This <SizeLabel size="Standard Body" /></div>
          <div className="context-menu-item"><Replace size={14} /> Replace <SizeLabel size="Standard Body" /></div>
          <div className="context-menu-item danger"><Trash2 size={14} /> Delete <SizeLabel size="Standard Body" /></div>
        </div>
      </ComponentViewer>
    </div>
    
    <ComponentViewer title="Horizontal Tabs" description="Top-level page switching." code={CODE_SNIPPETS.tabs} fullWidth>
      <div className="border-bottom w-100">
        <ul className="nav-tabs-horizontal mb-0">
          <li className="nav-item-h active">Dashboard</li>
          <li className="nav-item-h">Profile</li>
          <li className="nav-item-h">Settings</li>
        </ul>
      </div>
    </ComponentViewer>
    
    <ComponentViewer title="Table Pagination" description="Right-aligned navigation." code={CODE_SNIPPETS.pagination} fullWidth>
      <div className="w-100 bg-white p-3 border rounded">
        <div className="pagination-wrapper">
          <div className="pagination-nav">
            <button className="page-nav-link disabled"><ChevronsLeft size={16} /></button>
            <button className="page-nav-link disabled"><ChevronLeft size={16} /></button>
            <span className="page-number">2</span>
            <button className="page-nav-link"><ChevronRight size={16} /></button>
            <button className="page-nav-link"><ChevronsRight size={16} /></button>
          </div>
          <div className="page-select-wrapper">
            <select className="page-select"><option>500</option><option>1000</option></select>
            <ChevronDown size={14} className="page-select-icon" />
          </div>
        </div>
      </div>
    </ComponentViewer>
  </div>
);

const FormsSection = () => (
  <div className="d-flex flex-column gap-4">

    <ComponentViewer title="Tag Selector Dropdown" description="Custom multi-select dropdown for assigning tags, with custom tag creation. Uses 1.25 line-height for optimal list reading." code={CODE_SNIPPETS.tagSelector} fullWidth>
      <TagSelectorDemo />
    </ComponentViewer>

    <div className="bento-grid">
      <ComponentViewer title="Custom Controls (Toggles & Boxes)" description="Standardized custom form controls replacing default browser styling." code={CODE_SNIPPETS.customControls}>
        <div className="d-flex flex-column gap-3 w-100">
          
          {/* Custom Toggle Switch */}
          <label className="custom-switch-wrap mb-2">
            <div className="d-flex w-100 justify-content-between align-items-center">
               <span className="checkbox-label">Push Notifications <SizeLabel size="Standard Body" /></span>
               <div className="position-relative d-flex align-items-center">
                 <input type="checkbox" className="switch-input" defaultChecked />
                 <span className="switch-slider"></span>
               </div>
            </div>
          </label>

          {/* Custom Checkbox */}
          <label className="custom-checkbox-wrap mb-2">
            <input type="checkbox" className="custom-checkbox" defaultChecked />
            <span className="checkbox-label">Remember this device <SizeLabel size="Standard Body" /></span>
          </label>

          {/* Custom Radio */}
          <div className="d-flex flex-wrap gap-4 mt-2">
            <label className="custom-checkbox-wrap">
              <input type="radio" name="demo-radio" className="custom-radio" defaultChecked />
              <span className="checkbox-label">Option A</span>
            </label>
            <label className="custom-checkbox-wrap">
              <input type="radio" name="demo-radio" className="custom-radio" />
              <span className="checkbox-label">Option B</span>
            </label>
          </div>

        </div>
      </ComponentViewer>

      <ComponentViewer title="Document Title Field" description="High-prominence input for titles." code={CODE_SNIPPETS.docTitle}>
        <div className="w-100">
          <label className="form-label text-muted text-sm uppercase fw-bold mb-1">
              Document Name <SizeLabel size="Small Text" />
          </label>
          <div className="position-relative">
               <input type="text" className="input-title-large" defaultValue="Q3 Financial Report" />
               <div className="position-absolute end-0 top-50 translate-middle-y me-3 pointer-events-none"><SizeLabel size="H1" /></div>
          </div>
        </div>
      </ComponentViewer>

      <ComponentViewer title="Topic Selection Input" description="Multi-select topics with custom input." code={CODE_SNIPPETS.topicSelector}>
        <div className="w-100">
          <div className="position-relative mb-3">
            <input type="text" className="form-control input-action-pr" placeholder="Add custom topic..." />
            <div className="input-with-action-icon fw-bold" style={{fontSize: '18px', lineHeight: 1}}>+</div>
            <div className="position-absolute end-0 top-50 translate-middle-y me-5 pointer-events-none"><SizeLabel size="Base * Ratio" /></div>
          </div>
          
          <h4 className="fw-bold mb-1">
              1. Set Context <SizeLabel size="H4" />
          </h4>
          <p className="text-sm fw-bold text-muted mb-3">
              Select main topics (max 5) <SizeLabel size="Small Text" />
          </p>
          
          <div className="d-flex flex-wrap gap-2">
            <div className="topic-chip">first topic <SizeLabel size="Standard Body" /></div>
            <div className="topic-chip selected">second topic <SizeLabel size="Standard Body" /></div>
            <div className="topic-chip">third topic <SizeLabel size="Standard Body" /></div>
          </div>
        </div>
      </ComponentViewer>

      <ComponentViewer title="Input Fields" description="Standard form inputs." code={CODE_SNIPPETS.inputs}>
        <div className="d-flex flex-column gap-3 w-100">
          <div>
              <label className="form-label">Email Address <SizeLabel size="Standard Body" /></label>
              <div className="position-relative">
                  <input type="email" className="form-control" placeholder="name@example.com" />
                  <div className="position-absolute end-0 top-50 translate-middle-y me-3 pointer-events-none"><SizeLabel size="Base * Ratio" /></div>
              </div>
          </div>
          <div>
              <label className="form-label">Password <SizeLabel size="Standard Body" /></label>
              <div className="position-relative">
                  <input type="password" className="form-control" placeholder="••••••••" />
                  <div className="position-absolute end-0 top-50 translate-middle-y me-3 pointer-events-none"><SizeLabel size="Base * Ratio" /></div>
              </div>
          </div>
        </div>
      </ComponentViewer>
      
      <ComponentViewer title="OTP Input Group" description="For verification codes." code={CODE_SNIPPETS.otp}>
        <div className="d-flex flex-column align-items-center gap-2">
            <div className="d-flex justify-content-center gap-2">
              <input type="text" className="otp-input" defaultValue="4" />
              <input type="text" className="otp-input" defaultValue="2" />
              <input type="text" className="otp-input" />
              <input type="text" className="otp-input" />
            </div>
            <span className="text-muted text-sm"><SizeLabel size="H2" /> (Input Text)</span>
        </div>
      </ComponentViewer>
    </div>

    {/* RICH TEXT EDITOR MOCK */}
    <ComponentViewer title="Rich Text Editor" description="WYSIWYG editor example (Quill style)." code={CODE_SNIPPETS.rte} fullWidth>
      <div className="rich-text-editor w-100">
        <div className="rte-toolbar">
          <Bold size={16} className="rte-btn" />
          <Italic size={16} className="rte-btn" />
          <Underline size={16} className="rte-btn" />
          <span className="border-end mx-1"></span>
          <List size={16} className="rte-btn" />
          <ListOrdered size={16} className="rte-btn" />
          <span className="border-end mx-1"></span>
          <Link2 size={16} className="rte-btn" />
          <ImageIcon size={16} className="rte-btn" />
        </div>
        <div className="rte-content" contentEditable="true" suppressContentEditableWarning={true}>
          Start typing your content here... <SizeLabel size="Standard Body" />
        </div>
      </div>
    </ComponentViewer>
  </div>
);

const CardsSection = () => (
  <div className="d-flex flex-column gap-4">
    
    <ComponentViewer title="Page Container" description="Outer layout wrapper with header, soft shadow, and flexible scrolling content area." code={CODE_SNIPPETS.pageContainer} fullWidth>
      <div className="p-4 w-100" style={{backgroundColor: '#F2F4F7', borderRadius: '8px'}}>
        <div className="content-area" style={{height: '250px'}}>
          <div className="panel-header">
            <h3 className="fw-bold m-0">Assessments</h3>
            <button className="btn btn-primary">Create</button>
          </div>
          <div className="assessment-table p-0">
             <table className="ant-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Assessment 1</td><td><span className="badge badge-good">Active</span></td></tr>
                  <tr><td>Assessment 2</td><td><span className="badge badge-normal">Draft</span></td></tr>
                </tbody>
             </table>
          </div>
        </div>
      </div>
    </ComponentViewer>

    <ComponentViewer title="Data Table (Sticky Header)" description="Table using mathematical ratio. Header/Content: Standard Body. Status/Action/Icons: Small Text. Scroll to see sticky header." code={CODE_SNIPPETS.table} fullWidth>
      <div className="table-container w-100" style={{maxHeight: '220px'}}>
        <table className="ant-table">
          <thead>
            <tr>
              <th style={{width: '30%'}}>
                 <div className="ant-header-content">
                    Name <SizeLabel size="Standard Body" />
                    <div className="ant-icons">
                       <ArrowUp size={10} className="ant-icon-active" />
                       <ArrowDown size={10} />
                    </div>
                 </div>
              </th>
              <th>
                 <div className="ant-header-content">Role</div>
              </th>
              <th>
                 <div className="ant-header-content">Status</div>
              </th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>John Doe <SizeLabel size="Standard Body" /></td>
              <td>Developer</td>
              <td><span className="badge badge-good">Active <SizeLabel size="Small Text" /></span></td>
              <td className="text-end"><span className="table-action-link">Edit <SizeLabel size="Small Text" /></span></td>
            </tr>
             <tr>
              <td>Jane Smith</td>
              <td>Designer</td>
              <td><span className="badge badge-normal">Pending</span></td>
              <td className="text-end"><span className="table-action-link">Edit</span></td>
            </tr>
            <tr>
              <td>Mark Lee</td>
              <td>Manager</td>
              <td><span className="badge badge-good">Active</span></td>
              <td className="text-end"><span className="table-action-link">Edit</span></td>
            </tr>
             <tr>
              <td>Alice Cooper</td>
              <td>Analyst</td>
              <td><span className="badge badge-excellent">Top</span></td>
              <td className="text-end"><span className="table-action-link">Edit</span></td>
            </tr>
             <tr>
              <td>Bob Vance</td>
              <td>DevOps</td>
              <td><span className="badge badge-poor">Offline</span></td>
              <td className="text-end"><span className="table-action-link">Edit</span></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="p-2 text-muted text-sm border-top">Notice how Headers stay stuck to the top when scrolling.</div>
    </ComponentViewer>

    <div className="bento-grid">
      <ComponentViewer title="Standard Card" description="Basic content container with proportional elements." code={CODE_SNIPPETS.standardCard}>
        <div className="card-std w-100">
            <h4 className="fw-bold">Card Title <SizeLabel size="H4" /></h4>
            <p className="text-muted m-0">Standard card container content. <SizeLabel size="Standard Body" /></p>
        </div>
      </ComponentViewer>

      <ComponentViewer title="Match Status Badges" description="Color-coded status indicators." code={CODE_SNIPPETS.badges}>
        <div className="d-flex flex-column gap-3 w-100" style={{maxWidth: '500px', margin: '0 auto'}}>
          <div className="d-flex justify-content-between p-2 border-bottom"><div className="m-0 text-base fw-bold">A</div><span className="badge badge-excellent">Excellent <SizeLabel size="Small Text" /></span></div>
          <div className="d-flex justify-content-between p-2 border-bottom"><div className="m-0 text-base fw-bold">B</div><span className="badge badge-good">Good <SizeLabel size="Small Text" /></span></div>
          <div className="d-flex justify-content-between p-2 border-bottom"><div className="m-0 text-base fw-bold">C</div><span className="badge badge-normal">Normal <SizeLabel size="Small Text" /></span></div>
          <div className="d-flex justify-content-between p-2"><div className="m-0 text-base fw-bold">D</div><span className="badge badge-poor">Poor <SizeLabel size="Small Text" /></span></div>
        </div>
      </ComponentViewer>

      <ComponentViewer title="Information Card" description="Centered text, no icon." code={CODE_SNIPPETS.infoCard}>
        <div className="d-flex gap-3"><div className="information-card"><span>System Maintenance Scheduled <SizeLabel size="Standard Body" /></span></div></div>
      </ComponentViewer>
      
      <ComponentViewer title="Resume Highlight Card" description="Icon baseline alignment." code={CODE_SNIPPETS.resumeCard}>
        <div className="card-std w-100 d-flex gap-3 align-items-start" style={{maxWidth: '600px', margin: '0 auto'}}>
          <div className="icon-baseline"><Star size={18} className="text-warning" fill="currentColor" /></div>
          <div>
              <h4 className="fw-bold m-0 text-dark">Proactive Innovator <SizeLabel size="H4" /></h4>
              <p className="text-muted text-sm m-0 mt-1">Demonstrated initiative... <SizeLabel size="Small Text" /></p>
          </div>
        </div>
      </ComponentViewer>
      
      <ComponentViewer title="Selection Cards" description="Interactive selection states." code={CODE_SNIPPETS.selectionCard}>
        <div className="d-flex flex-column gap-3 w-100 m-0">
          <div className="card-select w-100 selected position-relative h-100">
            <div className="card-select-header"><h3 className="fw-bold">Plan Pro <SizeLabel size="H3" /></h3></div>
            <p className="text-center text-muted m-0">₹899/mo <SizeLabel size="Standard Body" /></p>
            <div className="position-absolute top-0 end-0 p-2 text-primary"><CheckCircle2 size={18} /></div>
          </div>
          <div className="card-select w-100 h-100">
            <div className="card-select-header"><h3 className="fw-bold">Enterprise <SizeLabel size="H3" /></h3></div>
            <p className="text-center text-muted m-0">Contact Us <SizeLabel size="Standard Body" /></p>
          </div>
        </div>
      </ComponentViewer>
      
      <ComponentViewer title="Role Selection (VWO)" description="Icon-based selection cards." code={CODE_SNIPPETS.roleCard}>
        <div className="d-flex flex-column gap-3 justify-content-center align-items-center flex-wrap w-100">
          <div className="VWOcard" style={{width: '100%', maxWidth: '200px', minHeight: '120px'}}><div className="d-flex align-items-center justify-content-center mb-2" style={{width:40,height:40,borderRadius:'50%',background:'#eef2ff',color:'var(--brand-primary)'}}><Layout size={20}/></div><p className="text-base m-0">Candidate <SizeLabel size="Standard Body" /></p></div>
          <div className="VWOcard orngBrdr" style={{width: '100%', maxWidth: '200px', minHeight: '120px'}}><div className="d-flex align-items-center justify-content-center mb-2" style={{width:40,height:40,borderRadius:'50%',background:'#fef2f2',color:'#ef4444'}}><CreditCard size={20}/></div><p className="text-base m-0">Organization <SizeLabel size="Standard Body" /></p></div>
        </div>
      </ComponentViewer>
    </div>

    <ComponentViewer title="Pricing Cards" description="Tiered subscription cards." code={CODE_SNIPPETS.pricingCard} fullWidth>
      <div className="row g-4 m-0 w-100" style={{maxWidth: '800px', margin: '0 auto'}}>
        <div className="col-12 col-sm-6">
          <div className="card-pricing standard">
              <div>
                  <h2 className="fw-bold">Standard <SizeLabel size="H2" /></h2>
                  <ul className="small list-unstyled text-muted d-flex flex-column gap-2 p-0 mt-3">
                      <li className="d-flex gap-2"><Check size={16} className="text-primary"/>Basic <SizeLabel size="Standard Body" /></li>
                  </ul>
              </div>
              <button className="btn btn-outline-primary w-100 mt-4">Choose</button>
          </div>
        </div>
        <div className="col-12 col-sm-6">
          <div className="card-pricing premium">
              <div className="badge-floating">Best Value <SizeLabel size="Small Text" /></div>
              <div>
                  <h2 className="fw-bold">Premium <SizeLabel size="H2" /></h2>
                  <ul className="small list-unstyled text-muted d-flex flex-column gap-2 p-0 mt-3">
                      <li className="d-flex gap-2"><Check size={16} className="text-success"/>Pro <SizeLabel size="Standard Body" /></li>
                  </ul>
              </div>
              <button className="btn btn-primary w-100 mt-4" style={{backgroundColor:'#198754',borderColor:'#198754'}}>Go Premium</button>
          </div>
        </div>
      </div>
    </ComponentViewer>

    <ComponentViewer title="Modal (Static Preview)" description="Standard modal dialog structure." code={CODE_SNIPPETS.modal} fullWidth>
        <div className="position-relative w-100" style={{height: '300px', background: '#f0f0f0', border: '1px dashed #ccc', borderRadius: '8px', maxWidth: '800px', margin: '0 auto'}}>
            <div className="custom-modal-overlay" style={{position: 'absolute', background: 'transparent'}}>
                <div className="custom-modal-dialog">
                    <div className="custom-modal-header">
                        <h3 className="fw-bold m-0">Edit User Details <SizeLabel size="H3" /></h3>
                        <button className="btn btn-icon"><X size={16} /></button>
                    </div>
                    <div className="custom-modal-body">
                         <p className="text-muted">Make changes to the user profile here. Click save when you're done. <SizeLabel size="Standard Body" /></p>
                         <div className="mb-3 mt-3">
                             <label className="form-label">Full Name <SizeLabel size="Standard Body" /></label>
                             <div className="position-relative">
                                 <input type="text" className="form-control" defaultValue="John Doe" />
                             </div>
                         </div>
                    </div>
                    <div className="custom-modal-footer">
                        <button className="btn btn-outline-primary">Cancel</button>
                        <button className="btn btn-primary">Save Changes</button>
                    </div>
                </div>
            </div>
        </div>
    </ComponentViewer>

    <ComponentViewer title="Toast Notification" description="Animated notification." code={CODE_SNIPPETS.toast} fullWidth>
        <div className="position-relative w-100" style={{height: '150px', background: '#f9fafb', border: '1px dashed #ccc', borderRadius: '8px'}}>
             <div className="toast-container" style={{position: 'absolute', bottom: '20px', right: '20px'}}>
                 <div className="toast anim-fade bg-white border rounded shadow-sm p-3 d-flex align-items-center gap-3" style={{minWidth: '300px'}}>
                     <CheckCircle2 className="text-success" size={20} />
                     <div className="d-flex flex-column">
                         <span className="fw-bold text-base">Success <SizeLabel size="Standard Body" /></span>
                         <span className="text-muted text-sm">Changes saved successfully. <SizeLabel size="Small Text" /></span>
                     </div>
                     <button className="btn-icon ms-auto"><X size={14}/></button>
                 </div>
             </div>
        </div>
    </ComponentViewer>

    <ComponentViewer title="Sticky Footer" description="Bottom fixed bar for actions." code={CODE_SNIPPETS.stickyBar} fullWidth>
        <div className="sticky-action-bar w-100" style={{maxWidth: '800px', margin: '0 auto'}}>
            <button className="btn btn-outline-primary">Cancel</button>
            <div className="d-flex align-items-center gap-3">
                <span className="text-base fw-bold">2 Items Selected <SizeLabel size="Standard Body" /></span>
                <button className="btn btn-primary">Apply Action</button>
            </div>
        </div>
    </ComponentViewer>

  </div>
);

const ModulesSection = () => (
  <div className="d-flex flex-column gap-4">
    
    <ComponentViewer title="Chat Interface" description="Message bubbles." code={CODE_SNIPPETS.chat} fullWidth>
      <div className="w-100 bg-light p-4 rounded border chat-container" style={{maxWidth: '700px', margin: '0 auto'}}>
        <div className="chat-msg received">Hello! Update available.<div className="text-muted text-sm">10:00 AM</div></div>
        <div className="chat-msg sent">Great news!<div className="text-muted text-sm">10:05 AM</div></div>
      </div>
    </ComponentViewer>

    <ComponentViewer title="Kanban Board" description="Task columns." code={CODE_SNIPPETS.kanban} fullWidth>
      <div className="kanban-board w-100">
        <div className="kanban-col">
          <div className="mb-3 text-base fw-bold text-muted">TO DO</div>
          <div className="kanban-item">Analyze Requirements</div>
        </div>
        <div className="kanban-col">
          <div className="mb-3 text-base fw-bold text-muted">DONE</div>
          <div className="kanban-item border-start border-4 border-success ps-3">Merge CSS Files</div>
        </div>
      </div>
    </ComponentViewer>

    <div className="bento-grid">
      <ComponentViewer title="Custom Tooltip" description="Hover effect." code={CODE_SNIPPETS.tooltip}>
        <div className="d-flex justify-content-center py-4">
          <div className="d-flex flex-column align-items-center">
            <div className="d-flex align-items-center justify-content-center mb-2 text-primary" style={{width:40,height:40,borderRadius:'50%',background:'#eef2ff'}}><HelpCircle size={20}/></div>
            <span className="text-sm fw-medium">Hover for Details</span>
          </div>
        </div>
      </ComponentViewer>
      
      <ComponentViewer title="List Header" description="Header for data lists." code={CODE_SNIPPETS.listHeader}>
        <div className="candListBox w-100 p-3" style={{margin: '0'}}>
          <div className="candListHdr border-bottom-none m-0 p-0">
             <div className="d-flex justify-content-between align-items-center w-100">
               <h4 className="m-0 text-base">Active Applications</h4>
               <button className="btn btn-primary btn-sm px-3">+ Add</button>
             </div>
          </div>
          <div className="p-4 text-muted text-center text-sm border-top mt-3">No applications found.</div>
        </div>
      </ComponentViewer>
    </div>
  </div>
);

const TypographySection = () => (
  <div className="d-flex flex-column gap-4">
    
    <ComponentViewer title="Heading Hierarchy" description="Semantic headings." code={CODE_SNIPPETS.headings} fullWidth>
      <div className="d-flex flex-column gap-2 w-100" style={{maxWidth: '800px', margin: '0 auto'}}>
        <div className="d-flex justify-content-between align-items-center">
          <h1 className="fw-bold m-0">Page Title (H1)</h1>
          <span className="badge badge-normal">Fluid ~24-31px</span>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-2">
          <h2 className="fw-bold m-0">Section Heading (H2)</h2>
          <span className="badge badge-normal">Fluid ~20-24px</span>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-2">
          <h3 className="fw-bold m-0">Card Title (H3)</h3>
          <span className="badge badge-normal">Fluid ~16-19px</span>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-2">
          <h4 className="fw-bold m-0">Sub-Label (H4)</h4>
          <span className="badge badge-normal">~14px (Base * Ratio&sup2;)</span>
        </div>
      </div>
    </ComponentViewer>

    <ComponentViewer title="Line Spacing (Leading)" description="Comparing tighter headings (1.14), readable body text (1.4), and looser captions (1.5)." code={CODE_SNIPPETS.lineHeight} fullWidth>
      <div className="d-flex flex-column gap-4 w-100" style={{maxWidth: '800px', margin: '0 auto'}}>
        <div className="p-4 border rounded bg-white position-relative">
          <span className="badge badge-good position-absolute top-0 end-0 m-3">Line-height: 1.14</span>
          <h3 className="fw-bold m-0 text-dark demo-highlight-primary">
            Enterprise Dashboard Overview<br/>
            Q3 Performance Metrics
          </h3>
        </div>
        <div className="p-4 border rounded bg-white position-relative">
          <span className="badge badge-normal position-absolute top-0 end-0 m-3">Line-height: 1.4</span>
          <p className="text-base text-muted m-0 demo-highlight-success">
            This dashboard provides a comprehensive view of our quarterly performance.<br/>
            Notice how the optimized line spacing makes reading multi-line data easier,<br/>
            while the tighter heading keeps the title visually grouped.
          </p>
        </div>
        <div className="p-4 border rounded bg-white position-relative">
          <span className="badge badge-poor position-absolute top-0 end-0 m-3">Line-height: 1.5</span>
          <p className="text-sm text-muted m-0 demo-highlight-danger">
            * Note: Small captions and footnotes use a looser 1.5 line height<br/>
            to ensure that tiny characters remain highly legible.
          </p>
        </div>
      </div>
    </ComponentViewer>

    <div className="bento-grid">
      <ComponentViewer title="Text Truncation" description="Handling long text in dense layouts." code={CODE_SNIPPETS.textTruncation}>
        <div className="d-flex flex-column gap-4 w-100">
          <div>
             <div className="text-sm fw-bold mb-1 text-muted">Standard Wrapping</div>
             <p className="text-base border p-2 bg-white rounded" style={{width: '200px'}}>
               This is a very long text string that will wrap to the next line naturally.
             </p>
          </div>
          <div>
             <div className="text-sm fw-bold mb-1 text-muted">Truncated (.text-truncate)</div>
             <p className="text-base border p-2 bg-white rounded text-truncate" style={{width: '200px'}}>
               This is a very long text string that will be cut off with an ellipsis because it doesn't fit.
             </p>
          </div>
        </div>
      </ComponentViewer>
      
      <ComponentViewer title="Text Sizes" description="Standard sizes." code={CODE_SNIPPETS.textSizes}>
        <div className="d-flex flex-column gap-4 w-100">
           <div>
              <p className="text-muted text-sm mb-1">Standard Body Text (11px / 0.6875rem)</p>
              <div className="d-flex flex-column gap-2">
                 <p className="text-base fw-normal m-0">Normal Weight (400)</p>
                 <p className="text-base fw-bold m-0">Bold Weight (700)</p>
              </div>
           </div>
           <hr className="border-light m-0" />
           <div>
              <p className="text-muted text-sm mb-1">Small Text (~9.77px / Base &divide; Ratio)</p>
              <div className="d-flex flex-column gap-2">
                 <p className="text-sm fw-normal m-0">Normal Weight (400)</p>
                 <p className="text-sm fw-bold m-0">Bold Weight (700)</p>
              </div>
           </div>
        </div>
      </ComponentViewer>
      
      <ComponentViewer title="Colors & Usage" description="Contextual usage. All shown in Base size (11px)." code={CODE_SNIPPETS.colors}>
        <div className="d-flex flex-column gap-3 w-100">
          <p className="text-primary text-base m-0">Primary (Links/Actions)</p>
          <p className="text-danger text-base m-0">Danger (Errors/Deletes)</p>
          <p className="text-warning text-base m-0">Warning (Alerts/Pending)</p>
          <p className="text-success text-base m-0">Success (Validations)</p>
          <p className="text-muted text-base m-0">Muted (Hints/Metadata)</p>
        </div>
      </ComponentViewer>
    </div>
  </div>
);

// ----------------------------------------------------------------------------------
// 5. ICON DATA & SECTION
// ----------------------------------------------------------------------------------

const ICON_MAP = {
  Menu, X, Download, Copy, Check, Type, MousePointer2, Layout, 
  CreditCard, MessageSquare, ChevronRight, Terminal, CheckCircle2, 
  Star, Briefcase, MapPin, Smile, User, Settings, Calendar, Edit, 
  AlertCircle, HelpCircle, Info, ArrowLeft, Building2, BadgeCheck, 
  CloudUpload, Mail, Zap, FileText, CheckCircle, Loader2, Sparkles, 
  Clock, Share2, UserPlus, Link, Linkedin, ThumbsUp, Lock, IndianRupee, 
  Twitter, Github, Facebook, Instagram, FileUser, Scan, Users, BookOpen, 
  Brain, CircleX, AlertTriangle, List, LogOut, Target, MoreVertical, Home,
  ChevronLeft, ChevronsLeft, ChevronsRight, ChevronDown, LayoutGrid,
  RefreshCw, Replace, MoreHorizontal, Filter, ArrowUp, ArrowDown, Plus, Trash2,
  BookMarked, Monitor, Smartphone, Ruler, Eye, Calculator, Layers
};

export const Icon = ({ name, size = 24, className = '', color, ...props }) => {
  const LucideIcon = ICON_MAP[name];
  if (!LucideIcon) return null;
  return <LucideIcon size={size} className={className} color={color} {...props} />;
};

const commonIcons = [
    { name: 'Grid View', icon: 'LayoutGrid' },
    { name: 'List View', icon: 'List' },
    { name: 'Regenerate', icon: 'RefreshCw' },
    { name: 'Replace', icon: 'Replace' },
    { name: 'More', icon: 'MoreHorizontal' },
    { name: 'ArrowLeft', icon: 'ArrowLeft' },
    { name: 'Briefcase', icon: 'Briefcase' },
    { name: 'Building2', icon: 'Building2' },
    { name: 'BadgeCheck', icon: 'BadgeCheck' },
    { name: 'UploadCloud', icon: 'CloudUpload' },
    { name: 'ChevronRight', icon: 'ChevronRight' },
    { name: 'User', icon: 'User' },
    { name: 'Mail', icon: 'Mail' },
    { name: 'MapPin', icon: 'MapPin' },
    { name: 'Calendar', icon: 'Calendar' },
    { name: 'Zap', icon: 'Zap' },
    { name: 'FileText', icon: 'FileText' },
    { name: 'CheckCircle', icon: 'CheckCircle' },
    { name: 'Loader2', icon: 'Loader2' },
    { name: 'Check', icon: 'Check' },
    { name: 'Sparkles', icon: 'Sparkles' },
    { name: 'X', icon: 'X' },
    { name: 'Info', icon: 'Info' },
    { name: 'Clock', icon: 'Clock' },
    { name: 'Download', icon: 'Download' },
    { name: 'Share2', icon: 'Share2' },
    { name: 'UserPlus', icon: 'UserPlus' },
    { name: 'Edit', icon: 'Edit' },
    { name: 'Link', icon: 'Link' },
    { name: 'WhatsApp', icon: 'MessageSquare' },
    { name: 'ThumbsUp', icon: 'ThumbsUp' },
    { name: 'Money', icon: 'IndianRupee' },
    { name: 'Lock', icon: 'Lock' },
    { name: 'Star', icon: 'Star' },
    { name: 'Scan', icon: 'Scan' },
    { name: 'FileUser', icon: 'FileUser' },
    { name: 'Users', icon: 'Users' },
    { name: 'Book', icon: 'BookOpen' },
    { name: 'Brain', icon: 'Brain' },
    { name: 'XCircle', icon: 'CircleX' },
    { name: 'Alert', icon: 'AlertTriangle' },
    { name: 'List', icon: 'List' },
    { name: 'LogOut', icon: 'LogOut' },
    { name: 'Target', icon: 'Target' },
    { name: 'MoreVertical', icon: 'MoreVertical' },
    { name: 'Home', icon: 'Home' },
    { name: 'Plus', icon: 'Plus' },
    { name: 'Monitor', icon: 'Monitor' },
    { name: 'Smartphone', icon: 'Smartphone' },
    { name: 'Ruler', icon: 'Ruler' },
];

const socialIcons = [
    { name: 'Linkedin', icon: 'Linkedin', color: '#0077b5' },
    { name: 'Github', icon: 'Github', color: '#333' },
    { name: 'Facebook', icon: 'Facebook', color: '#1877f2' },
    { name: 'Twitter', icon: 'Twitter', color: '#1da1f2' },
    { name: 'Instagram', icon: 'Instagram', color: '#e1306c' },
];

const IconsSection = () => (
  <div className="d-flex flex-column gap-4">
    <ComponentViewer title="Social Media Brand Icons" description="Official brand color icons." code={CODE_SNIPPETS.socialIcons} fullWidth>
      <div className="row g-3 w-100 m-0">
        {socialIcons.map((item) => (
          <div key={item.name} className="col-6 col-sm-4 col-md-3 col-lg-2">
            <div className="d-flex flex-column align-items-center justify-content-center p-3 border rounded bg-white h-100 w-100">
                <div className="mb-2"><Icon name={item.icon} color={item.color} /></div>
                <div className="text-sm text-muted font-monospace">{item.name}</div>
            </div>
          </div>
        ))}
      </div>
    </ComponentViewer>
    <ComponentViewer title="Iconography" description="Lucide React icons." code={CODE_SNIPPETS.iconography} fullWidth>
      <div className="row g-3 w-100 m-0">
        {commonIcons.map((item) => (
          <div key={item.name} className="col-6 col-sm-4 col-md-3 col-lg-2">
            <div className="d-flex flex-column align-items-center justify-content-center p-3 border rounded bg-white h-100 w-100">
                <div className="mb-2 text-secondary"><Icon name={item.icon} /></div>
                <div className="text-sm text-muted font-monospace">{item.name}</div>
            </div>
          </div>
        ))}
      </div>
    </ComponentViewer>
  </div>
);

// ==================================================================================
// 6. MAIN APP WRAPPER
// ==================================================================================
export default function App() {
  const [activeTab, setActiveTab] = useState('practices');

  useEffect(() => { document.title = "Lywo Style Guide"; }, []);

  const handleDownload = () => {
    const blob = new Blob([MERGED_STYLES], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'lywo-styles.css';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const navItems = [
    { id: 'practices', label: 'Best Practices', icon: <BookMarked size={18} /> },
    { id: 'typography', label: 'Typography', icon: <Type size={18} /> },
    { id: 'navigations', label: 'Navigations', icon: <MousePointer2 size={18} /> },
    { id: 'forms', label: 'Forms & Inputs', icon: <Layout size={18} /> },
    { id: 'cards', label: 'Cards & Containers', icon: <CreditCard size={18} /> },
    { id: 'modules', label: 'Modules & Chat', icon: <MessageSquare size={18} /> },
    { id: 'icons', label: 'Icons', icon: <Smile size={18} /> },
  ];

  return (
    <div className="ds-wrapper desktop-preview">
      <style>{MERGED_STYLES}</style>
      <header className="ds-header">
        <div className="ds-brand-row">
          <div className="d-flex align-items-center gap-2 text-primary fw-bold text-xl">
            <Layout size={24} />
            <span>Lywo Style Guide</span>
          </div>
          
          <div className="d-flex align-items-center gap-3">
             <button onClick={handleDownload} className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2">
               <Download size={14} /> Download CSS
             </button>
          </div>
        </div>
        <div className="ds-nav-row">
          <ul className="nav-tabs-horizontal">
            {navItems.map((item) => (
              <li key={item.id} className={`nav-item-h ${activeTab === item.id ? 'active' : ''}`} onClick={() => setActiveTab(item.id)}>
                {item.icon} {item.label}
              </li>
            ))}
          </ul>
        </div>
      </header>
      <main className="ds-content">
        <header className="mb-5">
          <h1 className="fw-bold text-capitalize mb-2">{activeTab}</h1>
          <p className="text-muted text-base">Guidelines, classes, and code snippets for {activeTab}.</p>
        </header>
        {activeTab === 'practices' && <BestPracticesSection />}
        {activeTab === 'navigations' && <NavigationsSection />}
        {activeTab === 'forms' && <FormsSection />}
        {activeTab === 'cards' && <CardsSection />}
        {activeTab === 'modules' && <ModulesSection />}
        {activeTab === 'typography' && <TypographySection />}
        {activeTab === 'icons' && <IconsSection />}
      </main>
    </div>
  );
}

